import { TrendingDown, HelpCircle } from 'lucide-react'
import { useUIStore } from '@/store'
import type { ModuleName } from '@/types'
import { NotificationBell } from './NotificationBell'
import { DataBackup } from './DataBackup'
import { useHelp } from '@/components/help/helpContext'
import { SignInButton } from '@/components/auth/SignInButton'
import { SignedInBadge } from '@/components/auth/SignedInBadge'
import { useAuth } from '@/auth/useAuth'
import { softNavigate } from '@/lib/softRouting'

const TABS: { id: ModuleName; label: string }[] = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'registry', label: 'Debt Registry' },
  { id: 'engine', label: 'Strategy Engine' },
  { id: 'journey', label: 'Path to Freedom' },
  { id: 'cashflow', label: 'Cash Flow' },
]

interface HeaderProps {
  user?: { name: string; email: string } | null
}

export function Header({ user = null }: HeaderProps) {
  const activeModule = useUIStore((s) => s.activeModule)
  const setActiveModule = useUIStore((s) => s.setActiveModule)
  const { openHelp } = useHelp()
  const { isAuthenticated } = useAuth()

  return (
    <header
      className="border-b border-slate-800 bg-slate-950/80 backdrop-blur sticky top-0 z-40"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="max-w-screen-2xl mx-auto px-3 sm:px-4 flex items-center gap-3 sm:gap-6 h-14">
        {/* Logo — clickable home link. v0.8.24: now soft-navigates via
            history.pushState (RootRouter subscribes to pathname changes
            via `useCurrentPathname`) so the AppShell + AuthProvider stay
            mounted. Pre-fix `<a href="/landing">` triggered a full reload
            which bounced through auth hydration + module re-imports and
            was the main source of the "loading is choppy" report. */}
        <button
          type="button"
          onClick={() => softNavigate('/landing')}
          className="flex items-center gap-2 text-white font-bold text-lg shrink-0 rounded-md px-1 -mx-1 hover:bg-slate-800/60 transition-colors"
          aria-label="DebtOS home — view the landing page"
          title="View landing page"
        >
          <TrendingDown className="w-5 h-5 text-emerald-400" />
          <span>DebtOS</span>
        </button>

        {/* Top nav tabs — desktop and tablet only, hidden on mobile (bottom nav takes over) */}
        <nav className="hidden md:flex items-center gap-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveModule(tab.id)}
              className={[
                'px-4 py-1.5 rounded-md text-sm font-medium transition-colors',
                activeModule === tab.id
                  ? 'bg-slate-800 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800/50',
              ].join(' ')}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="ml-auto flex items-center gap-1 sm:gap-3">
          <button
            onClick={openHelp}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            title="User Guide"
            aria-label="User Guide"
          >
            <HelpCircle className="w-5 h-5" />
          </button>
          <DataBackup />
          <NotificationBell />
          {user && <span className="hidden sm:inline text-sm text-slate-400">{user.name}</span>}
          {isAuthenticated ? <SignedInBadge /> : <SignInButton />}
        </div>
      </div>
    </header>
  )
}
